import { useEffect, useState, useCallback, memo, Fragment } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
// eslint-disable-next-line import/no-unresolved
import ESIInclude from './../esi';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import FilterSuggestions from '../components/filter-suggestions/FilterSuggestions';
import GridRow from '../components/grid/GridRow';
import GridColumn from '../components/grid/GridColumn';
import { Banner } from '../components/banners/Banner';
import Header from '../components/header/Header';
import SubCopy from '../components/header/sub-copy/SubCopy';
import { CorrectedSearchTerm } from '../components/header/corrected-search-term/CorrectedSearchTerm';
import ProductListing from '../components/product-listing/ProductListing';
import BackToTop from '../components/back-to-top/BackToTop';
import styles from './PLP.module.scss';
import constants from '../utils/constants';
import { useErrorContext } from '../context/ErrorContext';
import {
  dispatchMonetateManagedImpressions,
  useExperiencesContext,
} from '../components/experiences/Experiences';
import { usePageLoadReady } from '../hooks/usePageLoadReady';
import { dispatchCitrusAnalytics } from '../components/analytics/analyticsEvents';
import {
  BrandBanner,
  getBrandBanner,
} from '../components/brand-banner/BrandBanner';
import { AlternativeSearchTerms } from '../components/header/did-you-mean/AlternativeSearchTerms';
import { sortOptions } from '../components/sort/options';
import { getAriaTextIfPriceRange } from '../utils/index';
import { AdtechBanner } from '../components/banners/AdtechBanner';
import { LeftNavFacets } from '../components/facets/LeftNavFacets';
import { QuickViewWrapper } from '../components/quick-view/QuickView';
import {
  useBannerContext,
  useFacetContext,
  useHeaderPeepingContext,
  useSearchContext,
  useBreadcrumbContext,
  useApplicationSettingsContext,
  useProductContext,
  usePlpBodySentinelContext,
  useRouterContext,
} from '../context/PlpContext';
import Script from 'next/script';
import { useCachedBasketData } from '@JohnLewisPartnership/jl-design-system/utils/basket/cachedBasket';

const Compare = dynamic(
  () =>
    import(
      /* webpackChunkName: "compareComponent" */
      /* webpackPreload: true */
      '../components/compare/Compare'
    ),
  { ssr: false }
);

const importEnergyDrawer = () =>
  import(
    /* webpackChunkName: "energyDrawerComponent" */
    '../components/energy-drawer/EnergyDrawer'
  );

const importFilterDrawer = () =>
  import(
    /* webpackChunkName: "filterDrawerComponent" */
    '../components/filter-drawer/FilterDrawer'
  );

const FilterDrawer = dynamic(importFilterDrawer, { ssr: false });

const EnergyDrawer = dynamic(importEnergyDrawer, { ssr: false });

const M2MProductSelector = dynamic(
  () =>
    import(
      /* webpackChunkName: "m2mProductSelectorComponent" */
      '../components/m2m-product-selector/M2MProductSelector'
    ),
  { ssr: false }
);

export const usePlpViewData = () => {
  const applicationSettingsContext = useApplicationSettingsContext();
  const basketData = useCachedBasketData();
  const breadcrumbContext = useBreadcrumbContext();
  const errorContext = useErrorContext();
  const experiencesContext = useExperiencesContext();
  const facetContext = useFacetContext();
  const productContext = useProductContext();
  const router = useRouterContext();
  const searchContext = useSearchContext();

  const { isHeaderPeeping, setIsHeaderPeeping } = useHeaderPeepingContext();
  const onHeaderPeepingStateChange = (peeped) => {
    setIsHeaderPeeping(peeped);
  };

  const { setPlpBodySentinelRef } = usePlpBodySentinelContext();
  const storePlpBodySentinelRef = (el) => {
    setPlpBodySentinelRef(el);
  };

  return {
    applicationSettingsContext,
    basketData,
    breadcrumbContext,
    errorContext,
    experiencesContext,
    facetContext,
    headerPeepingContext: { isHeaderPeeping, onHeaderPeepingStateChange },
    plpBodySentinelContext: { storePlpBodySentinelRef },
    productContext,
    router,
    searchContext,
  };
};

export const PlpView = (props) => {
  const { heading, subHeading } = props;

  const {
    applicationSettingsContext: applicationSettings,
    errorContext,
    experiencesContext,
    facetContext,
    headerPeepingContext,
    plpBodySentinelContext,
    productContext,
    router,
    searchContext,
  } = usePlpViewData();

  const { products, totalNumberOfResults } = productContext;

  const { compareScriptUrl, compareCssUrl, quickViewBaseUrl } =
    applicationSettings;

  const { type, page = '1', listHead } = router.query;
  const isSearch = type === 'search';

  const {
    alternativeSearchSuggestions,
    correctedSearchTerm = undefined,
    correctionType,
    sortBy,
    term,
  } = searchContext;

  const {
    experiences,
    isAltImageImpressionsFlagOn,
    isFilterSuggestionsFlagOn,
    isFoundItFlagOn,
    isHybridBannerFeatureFlagOn,
    isNewAtbAnalyticsFlagOn,
    isQuickViewImpressionsFlagOn,
    isStandaloneCompareFlagOn,
    isTabletEnhancementsFlagOn,
    isTabletImpressionsFlagOn,
    monetateId,
    rawExperiencesHeader,
    isCuratedCategoriesFlagOn,
  } = experiencesContext;

  const { hasFacetsError = false, hasProductListError = false } = errorContext;

  const { isHeaderPeeping, onHeaderPeepingStateChange } = headerPeepingContext;
  const { storePlpBodySentinelRef } = plpBodySentinelContext;
  const [displayFilterDrawer, setDisplayFilterDrawer] = useState(false);
  const [displayM2MProductSelector, setDisplayM2MProductSelector] =
    useState(false);
  const [displayQuickAdd, setDisplayQuickAdd] = useState(false);
  const [quickAddData, setQuickAddData] = useState(null);
  const [quickViewData, setQuickViewData] = useState(null);
  const [quickViewAnalyticsData, setQuickViewAnalyticsData] = useState({
    product: {},
    event: {},
    additionalInfo: {},
  });
  const [renderFilterDrawer, setRenderFilterDrawer] = useState(false);
  const [renderSkipFilters, setRenderSkipFilters] = useState(true);
  const [m2mProductSelectorDetails, setM2MProductSelectorDetails] = useState({
    targetHref: null,
  });
  const [
    filterDrawerOpenedByPrimaryFacet,
    setFilterDrawerOpenedByPrimaryFacet,
  ] = useState(false);
  const [primaryFacetClassName, setPrimaryFacetClassName] = useState('');
  const [isTablet, setIsTablet] = useState(false);
  const [hasBreakpointBelow1024px, setHasBreakpointBelow1024px] =
    useState(false);
  const [tabletImpressionDispatched, setTabletImpressionDispatched] =
    useState(false);
  const [altImageImpressionDispatched, setAltImageImpressionDispatched] =
    useState(false);

  const sortByLabel = sortOptions.find(
    (sortOption) => sortOption.value === sortBy
  )?.label;

  const debounce = (fn, delay) => {
    let id;
    return (...args) => {
      if (id) clearTimeout(id);
      id = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  };
  const setBreakpointBasedFlags = () => {
    if (isTabletImpressionsFlagOn) {
      setIsTablet(
        window.innerWidth >= constants.MOBILE_MAX_WIDTH &&
          window.innerWidth <= constants.TABLET_MAX_WIDTH
      );
    }
    if (isAltImageImpressionsFlagOn) {
      setHasBreakpointBelow1024px(
        window.innerWidth <= constants.TABLET_MAX_WIDTH
      );
    }
  };

  const { applied, isPrimaryFacetInDiscoResponse, primaryFacet } = facetContext;

  const individualFilters = applied?.filterCombination
    ? applied.filterCombination.split('|')
    : [];
  let screenReaderFilters =
    individualFilters.length === 0
      ? 'No filters are applied'
      : 'Your applied filters are, ';

  individualFilters.forEach((filter) => {
    const [filterName, filterValue] = filter.split(':');
    const filterValueToRead =
      getAriaTextIfPriceRange(filterValue) || filterValue;
    screenReaderFilters += `${filterName} ${filterValueToRead}, `;
  });

  /**
   * Updates analytics data for a given product and ad ID.
   *
   * @param {string} adId - The ID of the advertisement.
   * @param {string} productId - The ID of the product.
   * @param {string} originator - The source of the analytics event (e.g., "QUICK_VIEW_ICON", "VIEW_OFFERS").
   */
  const updateAnalyticsData = (adId, productId, originator) => {
    const analyticsData = { product: {}, event: {} };

    if (adId) {
      analyticsData.product.citrusAdId = adId;
      dispatchCitrusAnalytics(productId, adId);
    }
    if (originator !== undefined) {
      switch (originator) {
        case 'QUICK_VIEW_ICON':
          analyticsData.additionalInfo = {
            interaction: 'plpQuickviewIcon',
          };
          break;
        case 'VIEW_OFFERS':
          analyticsData.additionalInfo = {
            interaction: 'plpViewoffers',
          };
          break;
        case 'ADD_TO_BASKET':
          analyticsData.additionalInfo = {
            interaction: 'plpAddToBasket',
          };
          break;
        default:
          break;
      }
    }
    setQuickViewAnalyticsData(analyticsData);
  };

  const handleQuickAddDisplay = useCallback(
    async (
      id,
      productUrl,
      swatchOrder,
      preselectedColour,
      adId,
      shouldShowOffersView = false,
      originator
    ) => {
      setDisplayQuickAdd(true);

      if (typeof window?.performance?.mark === 'function') {
        performance.mark('quickViewDisplayed-start');
      }

      updateAnalyticsData(adId, id, originator);

      if (isQuickViewImpressionsFlagOn) {
        dispatchMonetateManagedImpressions({
          monetateId,
          experiences,
          monetateFlagName: 'quickViewImpressionsEnabled',
        });
      }

      const product = products.find((p) => p.id === id);
      const swatch = product?.colorSwatches?.find(
        (colorSwatch) =>
          colorSwatch.color ===
          (preselectedColour || swatchOrder[0] || colours[0])
      );

      setQuickAddData({
        variantId: swatch?.id,
        id,
      });

      return Promise.resolve();
    },
    []
  );

  const handleQuickAddClose = () => {
    setQuickAddData(null);
    setDisplayQuickAdd(false);
  };

  const handleFilterDrawerClick = useCallback(() => {
    setRenderFilterDrawer(true);
    setDisplayFilterDrawer(true);
    setFilterDrawerOpenedByPrimaryFacet(false);
    setPrimaryFacetClassName('');
  }, []);

  const handlePrimaryFacetClick = useCallback((className) => {
    if (isPrimaryFacetInDiscoResponse) {
      setRenderFilterDrawer(true);
      setDisplayFilterDrawer(true);
      setFilterDrawerOpenedByPrimaryFacet(true);
      setPrimaryFacetClassName(className);
    }
  }, []);

  const handleFilterDrawerOpen = () => {
    const primaryFacetAccordionName = `accordion-summary-${primaryFacet.name
      .toLowerCase()
      .replace(/ /g, '-')}`;

    const primaryFacetElement = document.getElementById(
      primaryFacetAccordionName
    );

    if (primaryFacetElement) {
      primaryFacetElement.scrollIntoView();
    }
  };

  const handleFilterDrawerClose = useCallback(() => {
    setDisplayFilterDrawer(false);
    setTimeout(() => {
      setRenderFilterDrawer(false);
    }, 400);
  }, []);

  const handleQuickViewClick = useCallback(
    (
      id,
      selectedProductUrl,
      swatchOrder,
      selectedSwatch,
      adId,
      shouldScrollToPromoMsgs = false,
      originator
    ) =>
      new Promise((resolve) => {
        const product = products.find((p) => p.id === id);
        const swatch = selectedSwatch
          ? product?.colorSwatches?.find(
              (colorSwatch) => colorSwatch.color === selectedSwatch
            )
          : product?.colorSwatches[0];

        updateAnalyticsData(adId, id, originator);

        if (isQuickViewImpressionsFlagOn) {
          dispatchMonetateManagedImpressions({
            monetateId,
            experiences,
            monetateFlagName: 'quickViewImpressionsEnabled',
          });
        }

        if (typeof window?.performance?.mark === 'function') {
          performance.mark('quickViewDisplayed-start');
        }

        setQuickViewData({
          id,
          onClose: resolve,
          variantId: swatch?.id,
        });
      }),
    [products]
  );

  const onCloseQuickView = () => {
    quickViewData.onClose();
    return setQuickViewData(null);
  };

  const handleM2MProductSelectorClick = useCallback((targetHref) => {
    setM2MProductSelectorDetails({ targetHref });
    setDisplayM2MProductSelector(true);
  }, []);

  const onCloseM2MProductSelector = () => {
    setDisplayM2MProductSelector(false);
    setM2MProductSelectorDetails({ targetHref: null });
  };

  usePageLoadReady();

  const toggleFilterDrawer = () => {
    const maxWidthToUse = isTabletEnhancementsFlagOn
      ? constants.TABLET_MAX_WIDTH
      : constants.MOBILE_MAX_WIDTH;

    if (window.innerWidth >= maxWidthToUse) {
      handleFilterDrawerClose();
      setRenderSkipFilters(true);
    } else {
      setRenderSkipFilters(false);
    }
  };

  useEffect(() => {
    const mobileHeaderPeepHandler = ({ detail: { peeped = false } }) => {
      onHeaderPeepingStateChange(peeped);
    };
    window.addEventListener(
      'jl-events.browser.mobileHeaderPeepUpdated',
      mobileHeaderPeepHandler
    );

    const stickyHeaderPeepHandler = ({ detail: { peeped = false } }) => {
      onHeaderPeepingStateChange(peeped);
    };
    window.addEventListener(
      'jl-events.browser.stickyHeaderPeepUpdated',
      stickyHeaderPeepHandler
    );

    setRenderSkipFilters(window.innerWidth >= constants.MOBILE_MAX_WIDTH);
    setBreakpointBasedFlags();

    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        importFilterDrawer();
        importEnergyDrawer();
      });
    } else {
      importFilterDrawer();
      importEnergyDrawer();
    }

    window.addEventListener('resize', toggleFilterDrawer);

    const debouncedBreakpointHandler = debounce(setBreakpointBasedFlags, 500);
    window.addEventListener('resize', debouncedBreakpointHandler);

    return () => {
      window.removeEventListener(
        'jl-events.browser.mobileHeaderPeepUpdated',
        mobileHeaderPeepHandler
      );
      window.removeEventListener(
        'jl-events.browser.stickyHeaderPeepUpdated',
        stickyHeaderPeepHandler
      );
      window.removeEventListener('resize', toggleFilterDrawer);
      window.removeEventListener('resize', debouncedBreakpointHandler);
    };
  }, []);

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (quickAddData) {
        // Clear quick add data if the user navigates away from the page
        handleQuickAddClose();
        return false; // prevent the popstate
      }
      if (quickViewData) {
        // Clear quick view data if the user navigates away from the page
        onCloseQuickView();
        return false; // prevent the popstate
      }
      // For all URLs except /search, the page or chunk are the first parameter.
      // For /search, it's the 2nd parameter onwards (the first being `search-term`)
      const splitOnCharacter = as.startsWith('/search') ? '&' : '?';

      const currentPath = as.split(splitOnCharacter)[0];
      const previousPath = router.asPath.split(splitOnCharacter)[0];

      if (currentPath === previousPath) {
        // SSR reload for back button when the urls are the same
        router.replace(as);
        return false; // prevent the popstate
      }

      return true; // allow the popstate
    });

    return () => {
      router.beforePopState(() => true);
    };
  });

  useEffect(() => {
    if (isTabletImpressionsFlagOn && isTablet && !tabletImpressionDispatched) {
      dispatchMonetateManagedImpressions({
        monetateId,
        experiences,
        monetateFlagName: 'tabletImpressionsEnabled',
      });
      setTabletImpressionDispatched(true);
    }
  }, [isTablet]);

  useEffect(() => {
    if (
      isAltImageImpressionsFlagOn &&
      hasBreakpointBelow1024px &&
      !altImageImpressionDispatched
    ) {
      dispatchMonetateManagedImpressions({
        monetateId,
        experiences,
        monetateFlagName: 'altImageImpressionsEnabled',
      });
      setAltImageImpressionDispatched(true);
    }
  }, [hasBreakpointBelow1024px]);

  if (totalNumberOfResults === 0) {
    return (
      <Fragment>
        <GridRow id="js-plp-header" className={styles.plp__header}>
          <GridColumn key="search-header">
            <Header
              heading={heading}
              subHeading={subHeading}
              showNumberOfResults={false}
              pageType={type}
            />
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn>
            <SubCopy />
          </GridColumn>
        </GridRow>

        <ESIInclude
          src="/content-renderer/no-results?includeCoreCss=false&includeCoreJS=false&includePolyfills=false"
          id="plp-no-results"
        />
      </Fragment>
    );
  }

  let screenReaderUpdates =
    totalNumberOfResults !== 1
      ? `There are ${totalNumberOfResults} results, `
      : `There is 1 result, `;
  if (sortByLabel) {
    screenReaderUpdates += `Results are sorted by ${sortByLabel}, `;
  }
  if (screenReaderFilters) {
    screenReaderUpdates += screenReaderFilters;
  }

  if (isCuratedCategoriesFlagOn) {
    return (
      <Fragment>
        <GridRow id="js-plp-header" className={styles.plp__header}>
          <GridColumn key="search-header">
            <Header
              heading={heading}
              subHeading={subHeading}
              showNumberOfResults
              totalNumberOfResults={
                correctedSearchTerm ? 0 : totalNumberOfResults
              }
              alternativeSearchSuggestions={null}
              correctedSearchTerm={null}
            />
          </GridColumn>
          <GridColumn
            className={classNames({
              [styles.productListFullWidth]:
                hasProductListError || hasFacetsError,
              [styles.tabletFullWidth]: isTabletEnhancementsFlagOn,
            })}
          >
            <ProductListing
              handleDisplayM2MProductSelector={handleM2MProductSelectorClick}
              handleDisplayQuickAdd={handleQuickAddDisplay}
              handleQuickViewClick={handleQuickViewClick}
              handleFilterDrawerClick={handleFilterDrawerClick}
              handlePrimaryFacetClick={handlePrimaryFacetClick}
            />
          </GridColumn>

          <BackToTop />
        </GridRow>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <GridRow id="js-plp-header" className={styles.plp__header}>
        <GridColumn key="search-header">
          <Header
            heading={isSearch && listHead ? listHead : heading}
            subHeading={subHeading}
            showNumberOfResults
            totalNumberOfResults={
              correctedSearchTerm ? 0 : totalNumberOfResults
            }
            alternativeSearchSuggestions={
              alternativeSearchSuggestions &&
              alternativeSearchSuggestions.length > 0 ? (
                <AlternativeSearchTerms
                  alternativeSearchSuggestions={alternativeSearchSuggestions}
                />
              ) : null
            }
            correctedSearchTerm={
              isSearch ? (
                <CorrectedSearchTerm
                  correctedSearchTerm={correctedSearchTerm}
                  totalNumberOfResults={totalNumberOfResults}
                  originalSearchTerm={term}
                  correctionType={correctionType}
                />
              ) : null
            }
          />
          {page === '1' && (
            <Banner bannerId="seoTopBannerId" className="seobanners-top" />
          )}
          {screenReaderUpdates && (
            <span
              id="screen-reader-updates"
              className={styles.visuallyHidden}
              aria-live="assertive"
            >
              {screenReaderUpdates}
            </span>
          )}
        </GridColumn>

        {isFilterSuggestionsFlagOn && <FilterSuggestions pageType={type} />}
      </GridRow>

      {renderSkipFilters && (
        <SecondaryButton
          data-testid="skip-filters-link"
          tag="a"
          href="#main-content"
          className={classNames(styles.visuallyHidden, styles.visibleOnFocus)}
        >
          Skip Filters
        </SecondaryButton>
      )}

      <div ref={storePlpBodySentinelRef}></div>
      <GridRow
        id="js-plp-body"
        className={classNames(styles.plp__body, {
          [styles.plp__body__peeping]: isHeaderPeeping,
        })}
      >
        <LeftNavFacets />
        {renderFilterDrawer && (
          <FilterDrawer
            closeFilterDrawer={handleFilterDrawerClose}
            isMoreFiltersButton={!filterDrawerOpenedByPrimaryFacet}
            isPrimaryFacetMainFacet={filterDrawerOpenedByPrimaryFacet}
            onAfterOpen={
              filterDrawerOpenedByPrimaryFacet
                ? handleFilterDrawerOpen
                : undefined
            }
            openFilterDrawer={displayFilterDrawer}
            primaryFacetClassName={primaryFacetClassName}
            totalNumberOfResults={totalNumberOfResults}
          />
        )}
        <GridColumn
          colSm={12}
          colMd={12}
          colLg={9}
          colXl={9}
          className={classNames({
            [styles.productListFullWidth]:
              hasProductListError || hasFacetsError,
            [styles.tabletFullWidth]: isTabletEnhancementsFlagOn,
          })}
        >
          {isHybridBannerFeatureFlagOn && !isFoundItFlagOn && (
            <Banner bannerId="blockBannerId" className="hybridbanners" />
          )}

          <ProductListing
            handleDisplayM2MProductSelector={handleM2MProductSelectorClick}
            handleDisplayQuickAdd={handleQuickAddDisplay}
            handleQuickViewClick={handleQuickViewClick}
            handleFilterDrawerClick={handleFilterDrawerClick}
            handlePrimaryFacetClick={handlePrimaryFacetClick}
          />

          {page === '1' && (
            <Banner bannerId="seoBottomBannerId" className="seobanners" />
          )}
        </GridColumn>
        <EnergyDrawer />
        {isStandaloneCompareFlagOn ? (
          <jl-compare experiences-raw-string={rawExperiencesHeader} />
        ) : (
          <Compare />
        )}

        {quickViewData && (
          <QuickViewWrapper
            onClose={onCloseQuickView}
            pimProductId={quickViewData.id}
            initialVariantId={quickViewData.variantId}
            rawExperiencesHeader={rawExperiencesHeader}
            analyticsAdditionalInfo={JSON.stringify(quickViewAnalyticsData)}
            config="standard"
          />
        )}
        {displayQuickAdd && quickAddData && (
          <QuickViewWrapper
            modalTitle="Confirm colour and size"
            onClose={handleQuickAddClose}
            pimProductId={quickAddData.id}
            initialVariantId={quickAddData.variantId}
            rawExperiencesHeader={rawExperiencesHeader}
            analyticsAdditionalInfo={JSON.stringify({
              product: quickViewAnalyticsData.product,
            })}
            config="quickAdd"
          />
        )}
        {displayM2MProductSelector && (
          <M2MProductSelector
            isOpen={displayM2MProductSelector}
            onClose={onCloseM2MProductSelector}
            pdpUrl={m2mProductSelectorDetails.targetHref}
          />
        )}
        <BackToTop />
        <>
          <Script strategy="lazyOnload" src={compareScriptUrl} />
          <link href={compareCssUrl} rel="stylesheet" />
        </>
        <Script
          strategy="lazyOnload"
          src={`${quickViewBaseUrl}/quick-view.iife.js`}
        />
      </GridRow>
    </Fragment>
  );
};

export const BannerViewer = memo(
  ({
    brandName,
    breadcrumbs,
    isKevelBrandBannerFlagOn,
    isTopBannerFeatureFlagOn,
  }) => {
    let props = {};
    const router = useRouterContext();
    const { type, sortBy, facetId, page = '1' } = router.query;
    const { topBannerId } = useBannerContext();

    const banner = getBrandBanner(breadcrumbs);

    const displayAdtechBanner =
      type === 'browse' ||
      (type === 'search' && !topBannerId) ||
      (type === 'brand' && isKevelBrandBannerFlagOn);

    const hideAdtechBanner = !displayAdtechBanner && isTopBannerFeatureFlagOn;

    if (displayAdtechBanner) {
      const facetIdString = facetId
        ? encodeURIComponent(facetId.join('/'))
        : '';
      const pageString = `${facetIdString ? '/' : ''}page/${page}`;
      const sortByString = sortBy
        ? `${facetIdString || pageString ? '/' : ''}sortBy/${sortBy}`
        : '';

      props = {
        brandName,
        pathname: `${facetIdString}${pageString}${sortByString}`,
      };
    }

    return (
      <Fragment>
        {banner && <BrandBanner banner={banner} />}
        {displayAdtechBanner && <AdtechBanner {...props} />}
        {hideAdtechBanner && (
          <Banner bannerId="topBannerId" className="banners" />
        )}
      </Fragment>
    );
  }
);

BannerViewer.displayName = 'BannerViewer';

export const PLP = (props) => {
  /* istanbul ignore next */
  const { isKevelBrandBannerFlagOn, isTopBannerFeatureFlagOn } =
    useExperiencesContext();
  const { breadcrumbs } = useBreadcrumbContext();
  const { brandName } = props;

  return (
    <Fragment>
      <BannerViewer
        brandName={brandName}
        breadcrumbs={breadcrumbs}
        isKevelBrandBannerFlagOn={isKevelBrandBannerFlagOn}
        isTopBannerFeatureFlagOn={isTopBannerFeatureFlagOn}
      />
      <div className={styles.plp}>
        {
          <span
            id="add-to-wish-list-esi"
            data-test-id="wish-list-esi"
            data-mode="icon-placeholder"
          >
            <ESIInclude
              id="add-to-wishlist-component"
              src="/add-to-wishlist/exclude-cpa.html"
            />
          </span>
        }
        <PlpView {...props} />
      </div>
    </Fragment>
  );
};

export default PLP;
